import { useState, useRef, useEffect, useCallback } from "react";
import HearingTestButton from "../../../components/hearingTestButton/hearingTestButton";
import FormActionButton from "../../../components/formActionButton/formActionButton";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import PillTitle from "../../../components/pillTitle/pillTitle";
import HearingTestTips from "../../../components/hearingTestTips/hearingTestTips";
import StyledSelect from "../../../components/styledSelect/styledSelect";
import HeadphoneTypeSelector from "../../../components/headphoneTypeSelector/headphoneTypeSelector";
import { AGE_CATEGORY_OPTIONS, GENDER_OPTIONS } from "../../../type/demographics";
import type { AgeCategory, Gender, HeadphoneConnection, HeadphoneType } from "../../../type/demographics";
import "./tonalCalibration.scss";

export function StartNotice({ onConfirmed }: { onConfirmed: () => void }) {
  const { t } = useI18n();

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders" text={t("Badanie słuchu")} />

        <h2 className="mb-3">{t("Audiometria tonalna")}</h2>

        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <HearingTestButton
            type="button"
            className="btn btn-light btn-lg px-4"
            iconClassName="bi bi-play-circle"
            onClick={onConfirmed}
          >
            {t("Rozpocznij badanie")}
          </HearingTestButton>

          <div>
            <p className="panel--runtest__notice_text mb-4 pt-4 px-0 px-md-4">
              {t("Badanie audiometrii tonalnej polega na słuchaniu przez słuchawki dźwięków o różnej częstotliwości i głośności. Za każdym razem, gdy usłyszysz nawet bardzo cichy dźwięk, zaznacz swoją odpowiedź. Badanie pozwala określić najcichsze dźwięki, które jesteś w stanie usłyszeć.")}
            </p>
          </div>          

          <HearingTestTips />
        </div>
      </div>
    </div>
  );
}

type ParamsNoticeProps = {
  onConfirmed: () => void;
  onOpenParamsModal: () => void;
  onNewTest: () => void;
  addNoise: boolean;
  continuous: boolean;
  randomTest: boolean;
  adaptiveTest: boolean;
  gender: Gender;
  ageCategory: AgeCategory;
  headphoneType: HeadphoneType;
  headphoneConnection: HeadphoneConnection;
  onChangeGender: (value: Gender) => void;
  onChangeAgeCategory: (value: AgeCategory) => void;
  onChangeHeadphoneType: (value: HeadphoneType) => void;
  onChangeHeadphoneConnection: (value: HeadphoneConnection) => void;
};

export function ParamsNotice({
  onConfirmed,
  onOpenParamsModal,
  onNewTest,
  addNoise,
  continuous,
  randomTest,
  adaptiveTest,
  gender,
  ageCategory,
  headphoneType,
  headphoneConnection,
  onChangeGender,
  onChangeAgeCategory,
  onChangeHeadphoneType,
  onChangeHeadphoneConnection,
}: ParamsNoticeProps) {
  const { t } = useI18n();
  const genderOptions = GENDER_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));
  const ageCategoryOptions = AGE_CATEGORY_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));

  const activeParams = [
    { key: "addNoise", active: addNoise, icon: "bi-soundwave", label: t("Dodaj szum") },
    { key: "continuous", active: continuous, icon: "bi-volume-up-fill", label: t("Dźwięk ciągły") },
    { key: "randomTest", active: randomTest, icon: "bi-shuffle", label: t("Losowy test") },
    { key: "adaptiveTest", active: adaptiveTest, icon: "bi-activity", label: t("Test adaptacyjny") },
  ].filter(p => p.active);

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders2" text={t("Parametry testu")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>

        <h2 className="mb-3">{t("Parametry testu")}</h2>

        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <p className="mb-3 panel--runtest__notice_text pt-2">
            {t("Podanie tych danych pozwoli lepiej dostosować parametry badania, co zwiększy dokładność uzyskanych wyników.")}
          </p>

          <div className="params-notice__demographics">
            <StyledSelect
              label={t("Płeć")}
              icon="bi-person"
              value={gender}
              options={genderOptions}
              onChange={onChangeGender}
              variant="on-dark"
            />
            <StyledSelect
              label={t("Kategoria wiekowa")}
              icon="bi-calendar3"
              value={ageCategory}
              options={ageCategoryOptions}
              onChange={onChangeAgeCategory}
              variant="on-dark"
            />
            <HeadphoneTypeSelector
              headphoneType={headphoneType}
              headphoneConnection={headphoneConnection}
              onChangeHeadphoneType={onChangeHeadphoneType}
              onChangeHeadphoneConnection={onChangeHeadphoneConnection}
            />
          </div>

          <p className="mb-3 panel--runtest__notice_text pt-2">
            {t("W tym kroku możesz zmienić parametry testu lub pozostawić ustawienia domyślne.")}
          </p>

          <div className="d-flex flex-column-reverse flex-md-row justify-content-center gap-2">
            <HearingTestButton
              type="button"
              className="btn btn-outline-light btn-lg px-4"
              iconClassName="bi bi-sliders2"
              onClick={onOpenParamsModal}
            >
              {t("Parametry testu")}
            </HearingTestButton>

            <HearingTestButton
              type="button"
              className="btn btn-light btn-lg px-4"
              iconClassName="bi bi-arrow-right"
              onClick={onConfirmed}
            >
              {t("Dalej")}
            </HearingTestButton>
          </div>

          {activeParams.length > 0 && (
            <div className="params-notice__selected">
              {activeParams.map(p => (
                <span key={p.key} className="params-notice__badge">
                  <i className={`bi ${p.icon}`} aria-hidden="true" />
                  <span>{p.label}</span>
                </span>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export function CalibrationNotice({ onConfirmed, onNewTest }: { onConfirmed: () => void; onNewTest: () => void }) {
  const { t } = useI18n();

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders" text={t("Kalibracja")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>

        <h2 className="mb-3">{t("Przygotuj poziom odsłuchu")}</h2>

        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <p className="mb-4 panel--runtest__notice_text pt-3">
            {t("Czy poziom głośności jest ustawiony na 50%? To bardzo ważne dla prawidłowego wyniku testu.")}
          </p>
          <HearingTestButton
            type="button"
            className="btn btn-light btn-lg px-4"
            iconClassName="bi bi-check-circle"
            onClick={onConfirmed}
          >
            {t("Potwierdzam ustawienie głośności")}
          </HearingTestButton>
        </div>
      </div>
    </div>
  );
}

export default function Calibration({ onDone, onNewTest }: { onDone: () => void; onNewTest: () => void }) {
  const { t } = useI18n();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    audioCtxRef.current = new AudioContext();
    return () => { audioCtxRef.current?.close(); };
  }, []);

  const playTone = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.frequency.value = 1000;
    gain.gain.value = 0.002;

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    setTimeout(() => { osc.stop(); }, 1500);

    setHasPlayed(true);
  }, []);

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders" text={t("Kalibracja")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>

        <h2 className="mb-3">{t("Przygotuj poziom odsłuchu")}</h2>

        <div className="panel--runtest__notice_area">
          <p className="mb-4">
            {t("Załóż słuchawki i odtwórz sygnał testowy. Ustaw głośność tak, aby dźwięk był ledwo słyszalny.")}
            <br />{t("Jeśli otoczenie jest głośne, wynik może być zafałszowany.")}
          </p>

          <div className="d-grid gap-3 d-sm-flex justify-content-center">
            <HearingTestButton
              type="button"
              className="btn btn-outline-light btn-lg px-4"
              iconClassName="bi bi-play-fill"
              onClick={playTone}
            >
              {t("Odtwórz dźwięk")}
            </HearingTestButton>
            <HearingTestButton
              type="button"
              className="btn btn-light btn-lg px-4"
              iconClassName={`bi ${hasPlayed ? "bi-unlock-fill" : "bi-lock-fill"} calibration-btn__icon`}
              onClick={onDone}
              disabled={!hasPlayed}
              data-locked={!hasPlayed}
              tabIndex={!hasPlayed ? -1 : 0}
            >
              <span>{t("Słyszę, przejdź dalej")}</span>
            </HearingTestButton>
          </div>
        </div>
      </div>
    </div>
  );
}